import { useCallback } from 'react';

import { useQuery } from '@apollo/client';

import { taskFilterOptionsQuery, tasksQuery } from 'graphql-queries';
import { TaskFilterOptionType } from 'graphql-query-contracts';

type TaskSearchVariables = {
  groupIds: string[];
  search?: object;
  pagination?: { start: number; perPage: number };
  filter: object;
  preFilter?: object;
};

type Props = {
  channelId?: string;
  variables: TaskSearchVariables;
};

const ALL_FILTER_OPTION_TYPES = Object.values(TaskFilterOptionType);

export function useTaskListData({ channelId, variables }: Props) {
  const tasksResult = useQuery(tasksQuery, {
    skip: !channelId,
    variables,
    fetchPolicy: 'network-only',
  });
  const filterOptionsResult = useQuery(taskFilterOptionsQuery, {
    skip: !channelId,
    variables: {
      groupIds: variables.groupIds,
      options: ALL_FILTER_OPTION_TYPES,
    },
    fetchPolicy: 'network-only',
  });

  const refetchTasks = tasksResult.refetch;
  const refetchFilterOptions = filterOptionsResult.refetch;
  const refreshAfterTaskChange = useCallback(async () => {
    const tasksRefetch = refetchTasks();
    const filterOptionsRefetch = refetchFilterOptions();
    // a failed refresh keeps the lists already shown and must not fail the task change
    const filterOptionsRefresh = filterOptionsRefetch.catch(() => undefined);

    await Promise.all([tasksRefetch, filterOptionsRefresh]);
  }, [refetchTasks, refetchFilterOptions]);

  return {
    tasks: tasksResult.data?.tasks?.tasks,
    pageInfo: tasksResult.data?.tasks?.pageInfo,
    filterOptions: (
      filterOptionsResult.data ?? filterOptionsResult.previousData
    )?.taskFilterOptions,
    loading: tasksResult.loading,
    error: tasksResult.error,
    refreshAfterTaskChange,
  };
}
